// Version: 3.0 - Admin flat tag list only; Browse sidebar Tags-only; dark file/album UI
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { getStorage, ref, listAll, getDownloadURL, uploadBytesResumable, uploadBytes } from 'firebase/storage';
import GalleryFilterSidebar from './GalleryFilterSidebar';
import LightboxTagEditor from './LightboxTagEditor';
import LandingPage from './LandingPage';
import RecipesSection from './RecipesSection';
import FamilyHubSection from './FamilyHubSection';
import {
  hasSiteAccess,
  canReadPhotos,
  canUploadPhotos,
  canWritePhotoMetadata,
} from './rbacUtils';
import {
  displayTagLabel,
  normalizeGroupsForPicker,
  normalizeTagArray,
  normalizeStringList,
  normalizeFacetOptions,
  mergeFacetOptionsWithGalleryPhotos,
  emptyFacetState,
  photoMatchesFacetFilters,
  facetFilterEntryLabel,
  normalizeCameraList,
  FACET_ROW_META,
  hydrateFacetsFromLegacyTags,
} from './tagConfigUtils';
import { parseGalleryLocation } from './galleryQuery';
import './App.css';

/** Stable string for comparing facet picker state (dirty vs last synced from selection / save). */
function facetStateSignature(f) {
  const parts = [];
  for (const { key } of FACET_ROW_META) {
    const arr = Array.isArray(f?.[key])
      ? [...f[key]].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      : [];
    parts.push(`${key}:${arr.join('\u0001')}`);
  }
  return parts.join('\u0002');
}

let nextFacetFilterRuleId = 1;

function createFacetFilterRule(overrides = {}) {
  return {
    id: `facet-filter-rule-${nextFacetFilterRuleId++}`,
    operator: 'include',
    filters: emptyFacetState(),
    ...overrides,
  };
}

function facetStateHasSelections(f) {
  return Object.values(f || {}).some((v) => Array.isArray(v) && v.length > 0);
}

function filtersForRuleOperator(rule) {
  const filters = rule?.filters || emptyFacetState();
  if (rule?.operator !== 'exclude') return filters;

  const out = emptyFacetState();
  for (const { key } of FACET_ROW_META) {
    out[key] = (filters[key] || [])
      .map((v) => String(v).replace(/^!+/, '').trim())
      .filter(Boolean)
      .map((v) => `!${v}`);
  }
  return out;
}

function photoMatchesFacetFilterRules(metadata, rules) {
  const activeRules = (Array.isArray(rules) ? rules : []).filter((rule) =>
    facetStateHasSelections(rule?.filters)
  );
  if (activeRules.length === 0) return true;
  return activeRules.every((rule) =>
    photoMatchesFacetFilters(metadata, filtersForRuleOperator(rule))
  );
}

function rulesFromGalleryFilters(filters) {
  const rules = [];
  for (const { key } of FACET_ROW_META) {
    for (const value of filters?.[key] || []) {
      const state = emptyFacetState();
      state[key] = [value];
      rules.push(createFacetFilterRule({ filters: state }));
    }
  }
  return rules;
}

function photoIsUntagged(metadata) {
  const facets = hydrateFacetsFromLegacyTags(metadata || {});
  if (FACET_ROW_META.some(({ key }) => (facets[key] || []).length > 0)) return false;
  return normalizeTagArray(metadata?.tags).length === 0;
}

// Bucket id for config (no gs://). Env may be `my-bucket` or `gs://my-bucket`.
const rawStorageBucket = (process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '').trim();
const storageBucketId = rawStorageBucket.replace(/^gs:\/\//i, '');

// Firebase configuration - replace with your config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: storageBucketId || undefined,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Cloud Functions base URL - update with your region and project
const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL || 
  `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net`;

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
// Same bucket as `storageBucket` above, as explicit `gs://` (works for default and typical GCS bucket names).
const storage = storageBucketId
  ? getStorage(app, `gs://${storageBucketId}`)
  : getStorage(app);
storage.maxUploadRetryTime = 20 * 60 * 1000;
storage.maxOperationRetryTime = 20 * 60 * 1000;
const googleProvider = new GoogleAuthProvider();

const SKIP_AUTO_AUTH_KEY = 'whitson-skip-auto-auth';
const AUTO_AUTH_ATTEMPTED_KEY = 'whitson-auto-auth-attempted';

/** Served from `frontend/public/` (see project root `WhitsonFamilyPhotos.png` or logo asset). */
const HEADER_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/WhitsonFamilyPhotos.png`;

function HeaderBrand() {
  return (
    <div className="header-brand">
      <img
        src={HEADER_LOGO_SRC}
        alt=""
        decoding="async"
        className="header-logo"
      />
    </div>
  );
}

function normalizeMetadataObject(raw) {
  const empty = {
    tags: [],
    people: [],
    places: [],
    pets: [],
    general: [],
    events: [],
    holidays: [],
    cameras: [],
    albums: [],
    location: '',
    event: '',
  };
  if (!raw || typeof raw !== 'object') return { ...empty };
  const merged = { ...empty, ...raw };
  merged.tags = normalizeTagArray(merged.tags);
  merged.people = normalizeStringList(merged.people);
  merged.places = normalizeStringList(merged.places);
  merged.pets = normalizeStringList(merged.pets);
  merged.general = normalizeStringList(merged.general);
  merged.events = normalizeStringList(merged.events);
  merged.holidays = normalizeStringList(merged.holidays);
  merged.cameras = normalizeCameraList(merged.cameras);
  merged.albums = normalizeStringList(merged.albums);
  merged.location = typeof merged.location === 'string' ? merged.location.trim() : '';
  merged.event = typeof merged.event === 'string' ? merged.event.trim() : '';
  merged.hidden = merged.hidden === true;
  return merged;
}

function normalizeStoragePath(p) {
  if (!p || typeof p !== 'string') return p;
  const t = p.trim();
  try {
    return decodeURIComponent(t);
  } catch {
    return t;
  }
}

const RESERVED_GALLERY_SEGMENTS = new Set([
  'incoming',
  'staging',
  'production',
  'public',
  'config',
]);

/** Match Cloud Functions `normalizeToProductionGalleryPath` for API bodies and comparisons. */
function isB2GalleryPath(p) {
  return typeof p === 'string' && p.startsWith('b2/');
}

function sha256FromPath(path) {
  const match = String(path || '').match(/([0-9a-f]{64})(?:\.[a-z0-9]{1,8})?$/i);
  return match ? match[1].toLowerCase() : '';
}

/** Spare copy of a Backblaze object, stored in Google Cloud Storage. */
function gcsMirrorPath(storagePath) {
  if (!isB2GalleryPath(storagePath)) return '';
  const fileName = storagePath.slice(3);
  if (!fileName || fileName.includes('..') || fileName.startsWith('thumbs/')) return '';
  return `mirror/${fileName}`;
}

function preferB2WhenSameFile(list) {
  const photos = Array.isArray(list) ? list : [];
  const b2Shas = new Set();
  for (const photo of photos) {
    if (!isB2GalleryPath(photo?.path)) continue;
    const sha = sha256FromPath(photo.path);
    if (sha) b2Shas.add(sha);
  }
  if (b2Shas.size === 0) return photos;
  return photos.filter((photo) => {
    if (isB2GalleryPath(photo?.path)) return true;
    const sha = sha256FromPath(photo?.path);
    return !sha || !b2Shas.has(sha);
  });
}

function photoIdFromLocation() {
  try {
    const id = new URLSearchParams(window.location.search).get('photo') || '';
    return /^[0-9a-f]{64}$/i.test(id) ? id.toLowerCase() : '';
  } catch {
    return '';
  }
}

function setPhotoQuery(id) {
  const url = new URL(window.location.href);
  if (id) url.searchParams.set('photo', id);
  else url.searchParams.delete('photo');
  const next = `${url.pathname}${url.search}${url.hash}`;
  if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    window.history.replaceState({}, '', next);
  }
}

function isHeicPath(path) {
  return /\.(heic|heif)$/i.test(String(path || ''));
}

/** Image names the preview converter also accepts when the browser cannot decode the bytes (HEIC saved as .jpg). */
function isConvertiblePath(path) {
  return /\.(jpg|jpeg|png)$/i.test(String(path || ''));
}

/** JPEG preview URL for HEIC originals. The token comes from getPhotoIndex. */
function heicPreviewUrl(preview, storagePath, useMirror, forceConvert = false) {
  if (!preview?.token || !(isHeicPath(storagePath) || (forceConvert && isConvertiblePath(storagePath)))) return '';
  const source = useMirror || !isB2GalleryPath(storagePath) ? 'gcs' : 'b2';
  const fileName = useMirror ? gcsMirrorPath(storagePath) : source === 'b2' ? storagePath.slice(3) : storagePath;
  if (!fileName || fileName.includes('..')) return '';
  const params = new URLSearchParams({
    source,
    file: fileName,
    exp: String(preview.exp),
    token: preview.token,
  });
  return `${FUNCTIONS_BASE_URL}/getHeicPreview?${params.toString()}`;
}

/** JPEG still for a video. Browsers cannot paint a MOV frame in the grid. */
function videoPosterUrl(preview, storagePath, useMirror) {
  if (!preview?.token || !isSupportedVideoName(storagePath)) return '';
  const source = useMirror || !isB2GalleryPath(storagePath) ? 'gcs' : 'b2';
  const fileName = useMirror ? gcsMirrorPath(storagePath) : source === 'b2' ? storagePath.slice(3) : storagePath;
  if (!fileName || fileName.includes('..') || (!fileName.startsWith('videos/') && !fileName.startsWith('mirror/videos/'))) return '';
  const params = new URLSearchParams({
    source,
    file: fileName,
    exp: String(preview.exp),
    token: preview.token,
  });
  return `${FUNCTIONS_BASE_URL}/getVideoPoster?${params.toString()}`;
}

function galleryPathKey(path) {
  return toProductionGalleryPath(normalizeStoragePath(path));
}

/** Browser URL for a B2 object using the short-lived download token from getPhotoIndex. */
function b2AuthorizedFileUrl(download, storagePath) {
  if (!download || !isB2GalleryPath(storagePath)) return '';
  const fileName = storagePath.slice(3);
  if (!fileName || fileName.includes('..')) return '';
  const encodedName = fileName.split('/').map((part) => encodeURIComponent(part)).join('/');
  const base = String(download.downloadUrl || '').replace(/\/$/, '');
  const bucket = encodeURIComponent(download.bucketName || '');
  const token = encodeURIComponent(download.authorizationToken || '');
  if (!base || !bucket || !token) return '';
  return `${base}/file/${bucket}/${encodedName}?Authorization=${token}`;
}

function toProductionGalleryPath(p) {
  const x = normalizeStoragePath(p);
  if (!x) return x;
  if (isB2GalleryPath(x)) return x;
  if (x.startsWith('photos/production/') || x === 'photos/production') return x;
  if (x.startsWith('videos/production/') || x === 'videos/production') return x;
  if (x.startsWith('originals/')) {
    return `photos/production/${x.slice('originals/'.length)}`;
  }
  if (x.startsWith('videos/')) {
    const rest = x.slice('videos/'.length);
    const first = rest.split('/')[0];
    if (first && RESERVED_GALLERY_SEGMENTS.has(first)) {
      return x;
    }
    return `videos/production/${rest}`;
  }
  if (x.startsWith('photos/')) {
    const rest = x.slice('photos/'.length);
    const first = rest.split('/')[0];
    if (first && RESERVED_GALLERY_SEGMENTS.has(first)) {
      return x;
    }
    return `photos/production/${rest}`;
  }
  return x;
}

function isSupportedVideoName(name) {
  const extension = String(name || '').toLowerCase().split('.').pop();
  return ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', '3gp', 'mts'].includes(extension);
}

function isVideoMedia(photo, mediaUrl = '') {
  return (
    photo?.metadata?.fileType === 'video' ||
    isSupportedVideoName(photo?.path) ||
    isSupportedVideoName(photo?.name) ||
    isVideoUrl(mediaUrl)
  );
}

/** Best-effort timestamp from sidecar JSON so we can merge CDN refetches without losing newer edits. */
function facetMetadataFreshnessMs(meta) {
  if (!meta || typeof meta !== 'object') return 0;
  const parse = (v) => {
    if (v == null || typeof v !== 'string') return 0;
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : 0;
  };
  return Math.max(
    parse(meta.metadataUpdatedAt),
    parse(meta.metadataCreatedAt),
    parse(meta.processedAt)
  );
}

/** Rough count of user-facing facet / tag entries (for merge tie-breaks). */
function facetMetadataContentScore(meta) {
  const m = normalizeMetadataObject(meta);
  return (
    m.people.length +
    m.places.length +
    m.pets.length +
    m.general.length +
    m.events.length +
    m.holidays.length +
    m.cameras.length +
    normalizeTagArray(m.tags).length +
    (m.location ? 1 : 0) +
    (m.event ? 1 : 0) +
    m.albums.length
  );
}

/**
 * `loadPhotos()` refetches sidecars; caches can briefly lag behind saves. Merge rules:
 * - Prefer the payload with **more facet/tag content** (disk wins when richer).
 * - When the server payload has **fewer** tags than in-memory, keep in-memory only if it is
 *   **strictly fresher** (stale CDN / empty JSON); otherwise trust the server (intentional clears).
 */
function mergePhotoListPreferFresherMetadata(prevList, incomingList) {
  if (!Array.isArray(incomingList) || incomingList.length === 0) {
    return incomingList;
  }
  const prevByPath = new Map(
    (Array.isArray(prevList) ? prevList : []).map((p) => [
      toProductionGalleryPath(normalizeStoragePath(p.path)),
      p,
    ])
  );
  return incomingList.map((incoming) => {
    const key = toProductionGalleryPath(normalizeStoragePath(incoming.path));
    const old = prevByPath.get(key);
    if (!old?.metadata) return incoming;
    const oldMs = facetMetadataFreshnessMs(old.metadata);
    const newMs = facetMetadataFreshnessMs(incoming.metadata);
    const oldScore = facetMetadataContentScore(old.metadata);
    const newScore = facetMetadataContentScore(incoming.metadata);

    if (newScore > oldScore) {
      return incoming;
    }
    if (newScore < oldScore && oldMs > newMs) {
      return {
        ...incoming,
        metadata: normalizeMetadataObject(old.metadata),
        isRestricted: old.isRestricted,
      };
    }
    if (newScore === oldScore && oldMs > newMs) {
      return {
        ...incoming,
        metadata: normalizeMetadataObject(old.metadata),
        isRestricted: old.isRestricted,
      };
    }
    return incoming;
  });
}

async function readResponseJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    const err = new Error(
      response.ok
        ? 'Server returned non-JSON response.'
        : `Save failed (HTTP ${response.status}).`
    );
    throw err;
  }
}

function isVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const u = url.toLowerCase();
  return ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.m4v', '.3gp'].some((ext) =>
    u.endsWith(ext)
  );
}

function PersonIcon() {
  return (
    <svg
      className="icon-btn-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

/** Doorway + arrow stepping out (sign out). */
function SignOutIcon() {
  return (
    <svg
      className="icon-btn-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [stagingPhotos, setStagingPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tagConfig, setTagConfig] = useState({
    groups: [],
    tags: [],
    people: [],
    places: [],
    pets: [],
    general: [],
    events: [],
    holidays: [],
    cameras: [],
  });
  const [facetFilterRules, setFacetFilterRules] = useState(() => [createFacetFilterRule()]);
  const [untaggedOnly, setUntaggedOnly] = useState(false);
  const [showStaging, setShowStaging] = useState(false);
  const fileInputRef = useRef(null);
  /** Bumps on each `loadPhotos` start so stale async completions cannot overwrite state. */
  const loadPhotosSeqRef = useRef(0);
  const openedShareIdRef = useRef('');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [assignFacets, setAssignFacets] = useState(() => emptyFacetState());
  /** Latest assignment form snapshot; Save reads this to avoid stale React state closures. */
  const assignFacetsRef = useRef(emptyFacetState());
  const [assignmentSavedSig, setAssignmentSavedSig] = useState(() =>
    facetStateSignature(emptyFacetState())
  );
  const [facetSidebarSaving, setFacetSidebarSaving] = useState(false);
  const [facetSidebarMessage, setFacetSidebarMessage] = useState(null);
  const [thumbnailsPerPage, setThumbnailsPerPage] = useState(100);
  const [galleryPage, setGalleryPage] = useState(1);
  const [mediaUrlByPath, setMediaUrlByPath] = useState({});
  const [mediaUrlAttemptsByPath, setMediaUrlAttemptsByPath] = useState({});
  const [b2Download, setB2Download] = useState(null);
  const [heicPreview, setHeicPreview] = useState(null);
  const [posterFailed, setPosterFailed] = useState({});
  const [gcsFallback, setGcsFallback] = useState({});
  /** Image names the browser could not decode; these go through the preview converter. */
  const [convertFallback, setConvertFallback] = useState({});
  const [mirrorUrlByPath, setMirrorUrlByPath] = useState({});
  const [hiddenPhotos, setHiddenPhotos] = useState([]);
  const [hiddenAccess, setHiddenAccess] = useState({ canHide: false, canView: false, viewers: [] });
  const [showHiddenView, setShowHiddenView] = useState(false);
  const [viewerDraft, setViewerDraft] = useState('');
  const [hiddenSaving, setHiddenSaving] = useState(false);
  const [hiddenMessage, setHiddenMessage] = useState('');
  const [lightboxPath, setLightboxPath] = useState(null);
  const galleryQuery = useMemo(() => parseGalleryLocation(window.location.search), []);
  const [slideRemaining, setSlideRemaining] = useState(0);
  const presentationStartedRef = useRef(false);
  const [devicePhotoIds, setDevicePhotoIds] = useState(null);
  const [linkedPhoto, setLinkedPhoto] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  /** @type {Record<string, unknown>} */
  const [tokenClaims, setTokenClaims] = useState({});
  /** 'landing' | 'photos' | 'recipes' | 'forum' | 'contacts' | 'calendar' */
  const [activeModule, setActiveModule] = useState(() =>
    parseGalleryLocation(window.location.search).presentation ? 'photos' : null
  );

  const pickerGroups = useMemo(() => normalizeGroupsForPicker(tagConfig), [tagConfig]);

  const facetOptions = useMemo(
    () => mergeFacetOptionsWithGalleryPhotos(normalizeFacetOptions(tagConfig), photos),
    [tagConfig, photos]
  );

  const facetFilterChipEntries = useMemo(() => {
    const rows = [];
    for (const rule of facetFilterRules) {
      const isExclude = rule.operator === 'exclude';
      for (const { key: k, label } of FACET_ROW_META) {
        for (const v of rule.filters?.[k] || []) {
          rows.push({
            ruleId: rule.id,
            k,
            label,
            v,
            displayValue: isExclude ? `!${v}` : v,
          });
        }
      }
    }
    return rows;
  }, [facetFilterRules]);

  const totalGalleryPages = useMemo(
    () => Math.max(1, Math.ceil(filteredPhotos.length / thumbnailsPerPage)),
    [filteredPhotos.length, thumbnailsPerPage]
  );

  const galleryPageOptions = useMemo(
    () => Array.from({ length: totalGalleryPages }, (_, i) => i + 1),
    [totalGalleryPages]
  );

  useEffect(() => {
    setGalleryPage((prev) => Math.min(Math.max(1, prev), totalGalleryPages));
  }, [totalGalleryPages]);

  const currentPageStart = (galleryPage - 1) * thumbnailsPerPage;
  const currentPageEnd = currentPageStart + thumbnailsPerPage;

  const visiblePhotos = useMemo(
    () => filteredPhotos.slice(currentPageStart, currentPageEnd),
    [filteredPhotos, currentPageStart, currentPageEnd]
  );

  // Admin tag list: `photos/config/available-tags.json` → `{ "tags": [ "..." ] }` (see setup-initial-tags.sh).
  const loadAvailableTags = useCallback(async () => {
    if (!user) return;
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${FUNCTIONS_BASE_URL}/getAvailableTags`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!response.ok) throw new Error(`getAvailableTags ${response.status}`);
      const data = await response.json();
      setTagConfig({
        groups: Array.isArray(data.groups) ? data.groups : [],
        tags: Array.isArray(data.tags) ? data.tags : [],
        people: Array.isArray(data.people) ? data.people : [],
        places: Array.isArray(data.places) ? data.places : [],
        pets: Array.isArray(data.pets) ? data.pets : [],
        general: Array.isArray(data.general) ? data.general : [],
        events: Array.isArray(data.events) ? data.events : [],
        holidays: Array.isArray(data.holidays) ? data.holidays : [],
        cameras: Array.isArray(data.cameras) ? data.cameras : [],
      });
    } catch (error) {
      console.error('Error loading available tags:', error);
    }
  }, [user]);

  const rememberGcsMirror = useCallback(async (storagePath, mode) => {
    const mirror = gcsMirrorPath(storagePath);
    if (!mirror) return;
    const preview =
      mode === 'preview' || (mode !== 'file' && (isHeicPath(storagePath) || isSupportedVideoName(storagePath)));
    if (preview) {
      setGcsFallback((prev) => (prev[storagePath] ? prev : { ...prev, [storagePath]: true }));
      return;
    }
    try {
      const url = await getDownloadURL(ref(storage, mirror));
      setMirrorUrlByPath((prev) => (prev[storagePath] === url ? prev : { ...prev, [storagePath]: url }));
    } catch (error) {
      console.warn('Google Cloud Storage mirror is not ready for this file yet.');
    }
  }, []);

  const loadPhotos = useCallback(async () => {
    if (!user || !user.email) {
      return;
    }
    const seq = ++loadPhotosSeqRef.current;
    let b2Photos = [];
    try {
      let indexByPath = new Map();
      let indexRows = [];
      try {
        const idToken = await user.getIdToken();
        const idxRes = await fetch(`${FUNCTIONS_BASE_URL}/getPhotoIndex`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (idxRes.ok) {
          const idxData = await idxRes.json();
          const rows = Array.isArray(idxData?.photos) ? idxData.photos : [];
          indexRows = rows;
          const b2Bundle = idxData?.b2 && typeof idxData.b2 === 'object' ? idxData.b2 : null;
          setB2Download(b2Bundle?.download || null);
          setHeicPreview(idxData?.heicPreview || null);
          const access = idxData?.hiddenAccess && typeof idxData.hiddenAccess === 'object'
            ? idxData.hiddenAccess
            : { canHide: false, canView: false, viewers: [] };
          setHiddenAccess({
            canHide: access.canHide === true,
            canView: access.canView === true,
            viewers: Array.isArray(access.viewers) ? access.viewers : [],
          });
          if (access.canView !== true) setShowHiddenView(false);
          const hiddenRows = access.canView === true && Array.isArray(idxData?.hiddenPhotos)
            ? idxData.hiddenPhotos
            : [];
          setHiddenPhotos(preferB2WhenSameFile(hiddenRows.map((row) => {
            const mediaPathNorm = normalizeStoragePath(row.path);
            const metadata = normalizeMetadataObject(row?.metadata);
            if (isB2GalleryPath(mediaPathNorm)) {
              return { ...row, path: mediaPathNorm, metadata: { ...metadata, hidden: true } };
            }
            return {
              name: row.name || mediaPathNorm.split('/').pop() || mediaPathNorm,
              url: '',
              path: mediaPathNorm,
              metadata: { ...metadata, hidden: true },
              isRestricted: metadata.restricted?.length > 0 || false,
            };
          })));
          b2Photos = Array.isArray(b2Bundle?.photos) ? b2Bundle.photos : [];
          indexByPath = new Map(
            rows.map((x) => [
              toProductionGalleryPath(normalizeStoragePath(x.path)),
              x?.metadata && typeof x.metadata === 'object' ? x.metadata : null,
            ])
          );
        } else {
          console.warn(`getPhotoIndex returned ${idxRes.status}; loading without sidecar metadata fallback.`);
        }
      } catch (idxErr) {
        console.warn('getPhotoIndex failed; loading without sidecar metadata fallback.', idxErr);
      }

      // Fast path: use indexed rows directly and skip expensive recursive bucket traversal.
      if (indexRows.length > 0) {
        const indexedPhotoList = indexRows.map((x) => {
          const mediaPathNorm = normalizeStoragePath(x.path);
          const m = normalizeMetadataObject(x?.metadata && typeof x.metadata === 'object' ? x.metadata : null);
          return {
            name: mediaPathNorm.split('/').pop() || mediaPathNorm,
            url: '',
            path: mediaPathNorm,
            metadata: m,
            isRestricted: m.restricted?.length > 0 || false,
          };
        });
        if (seq !== loadPhotosSeqRef.current) return;
        setPhotos((prev) =>
          mergePhotoListPreferFresherMetadata(prev, preferB2WhenSameFile(indexedPhotoList.concat(b2Photos)))
        );
        return;
      }

      /**
       * Gallery data + filtering: each item loads its sidecar `*.json` next to the media file under
       * production media roots. Facet filters run in-memory on that payload. At large scale you would add a
       * queryable index (e.g. Firestore) fed by the same writes, while keeping these JSON files as
       * the per-asset source of truth for backup / re-import.
       */
      const productionRefs = [ref(storage, 'photos/production/'), ref(storage, 'videos/production/')];

      // Recursively get all media and check access restrictions
      const photoList = [];
      const processFolder = async (folderRef) => {
        const folderResult = await listAll(folderRef);
        
        // Process files and metadata
        const fileMap = new Map(); // Map to store files and their metadata
        
        for (const itemRef of folderResult.items) {
          const fileName = itemRef.name;
          const baseName = fileName.split('.').slice(0, -1).join('.');
          const extension = fileName.split('.').pop()?.toLowerCase();
          
          // Check if it's a media file
          if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', '3gp'].includes(extension)) {
            if (!fileMap.has(baseName)) {
              fileMap.set(baseName, {});
            }
            fileMap.get(baseName).mediaFile = itemRef;
            fileMap.get(baseName).extension = extension;
          }
          
          // Check if it's a metadata JSON file
          if (extension === 'json') {
            if (!fileMap.has(baseName)) {
              fileMap.set(baseName, {});
            }
            fileMap.get(baseName).metadataFile = itemRef;
          }
        }
        
        // Process each file and check access
        for (const [, files] of fileMap.entries()) {
          if (!files.mediaFile) continue; // Skip if no media file found

          const mediaPathNorm = normalizeStoragePath(files.mediaFile.fullPath);
          const mediaPathProd = toProductionGalleryPath(mediaPathNorm);
          let metadata = indexByPath.get(mediaPathProd) || null;
          let hasAccess = true; // Default: all approved users have access

          // Index-first: avoid per-photo sidecar JSON fetches on initial load for fast startup.
          // Missing index rows simply get empty metadata until reindex/update fills them in.

          // Check if photo is restricted
          if (metadata?.restricted && Array.isArray(metadata.restricted) && metadata.restricted.length > 0) {
            // Photo is restricted - check if user's email is in the restricted list
            hasAccess = metadata.restricted.includes(user.email.toLowerCase());
          }
          
          // Only add photo if user has access
          if (hasAccess) {
            const metaNorm = normalizeMetadataObject(
              metadata && typeof metadata === 'object' ? metadata : null
            );
            const photoData = {
              name: files.mediaFile.name,
              url: '',
              path: mediaPathNorm,
              metadata: metaNorm,
              isRestricted: metaNorm.restricted?.length > 0 || false,
            };
            photoList.push(photoData);
          }
        }
        
        // Recursively process subfolders
        for (const prefixRef of folderResult.prefixes) {
          await processFolder(prefixRef);
        }
      };
      
      await Promise.all(productionRefs.map((productionRef) => processFolder(productionRef)));
      if (seq !== loadPhotosSeqRef.current) {
        return;
      }
      setPhotos((prev) => mergePhotoListPreferFresherMetadata(prev, preferB2WhenSameFile(photoList.concat(b2Photos))));
    } catch (error) {
      console.error('Error loading photos:', error);
      if (seq !== loadPhotosSeqRef.current) {
        return;
      }
      if (b2Photos.length > 0) {
        setPhotos((prev) => mergePhotoListPreferFresherMetadata(prev, b2Photos));
      }
      // Keep the last good gallery: transient failures (parallel refetches after upload, network)
      // used to clear state here and made tags disappear until a hard refresh.
    }
  }, [user]);

  // Lazy-load signed URLs only for currently visible thumbnails to reduce startup latency.
  useEffect(() => {
    if (!Array.isArray(visiblePhotos) || visiblePhotos.length === 0) return;
    let cancelled = false;
    const MAX_ATTEMPTS = 4;
    const BATCH_SIZE = 12;
    const missing = visiblePhotos
      .map((p) => normalizeStoragePath(p.path))
      .filter(
        (p) => p && !isB2GalleryPath(p) && !mediaUrlByPath[p] && (mediaUrlAttemptsByPath[p] || 0) < MAX_ATTEMPTS
      );
    if (missing.length === 0) return;

    (async () => {
      const updates = {};
      const failed = [];
      for (let i = 0; i < missing.length; i += BATCH_SIZE) {
        const batch = missing.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(async (path) => {
            try {
              updates[path] = await getDownloadURL(ref(storage, path));
            } catch (e) {
              failed.push(path);
              console.warn(`Could not load media URL for ${path}:`, e);
            }
          })
        );
      }
      if (cancelled) return;
      if (failed.length > 0) {
        setMediaUrlAttemptsByPath((prev) => {
          const next = { ...prev };
          for (const path of failed) {
            next[path] = (next[path] || 0) + 1;
          }
          return next;
        });
      }
      if (Object.keys(updates).length > 0) {
        setMediaUrlByPath((prev) => ({ ...prev, ...updates }));
        setPhotos((prev) =>
          prev.map((p) => {
            const key = normalizeStoragePath(p.path);
            const nextUrl = updates[key];
            return nextUrl ? { ...p, url: nextUrl } : p;
          })
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visiblePhotos, mediaUrlByPath, mediaUrlAttemptsByPath]);

  // Reset URL load retry counters when visible page set changes meaningfully (filters/paging switch).
  useEffect(() => {
    const visibleSet = new Set(
      visiblePhotos.map((p) => normalizeStoragePath(p.path)).filter(Boolean)
    );
    setMediaUrlAttemptsByPath((prev) => {
      const next = {};
      for (const key of Object.keys(prev)) {
        if (visibleSet.has(key) && !mediaUrlByPath[key]) next[key] = prev[key];
      }
      return next;
    });
  }, [visiblePhotos, mediaUrlByPath]);

  const loadStagingPhotos = useCallback(async () => {
    try {
      if (!user || !isApprover) {
        return;
      }

      const idToken = await user.getIdToken();
      const response = await fetch(`${FUNCTIONS_BASE_URL}/getStagingPhotos`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load staging photos');
      }

      const data = await response.json();
      setStagingPhotos(data.photos || []);
    } catch (error) {
      console.error('Error loading staging photos:', error);
    }
  }, [user, isApprover]);

  useEffect(() => {
    getRedirectResult(auth).catch((e) => {
      console.warn('getRedirectResult:', e?.code || e);
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        setUser(currentUser);
        if (currentUser) {
          try {
            sessionStorage.removeItem(SKIP_AUTO_AUTH_KEY);
            sessionStorage.removeItem(AUTO_AUTH_ATTEMPTED_KEY);
          } catch (e) {
            /* private mode */
          }
          // Check if user is authorized (has custom claim)
          const idTokenResult = await currentUser.getIdTokenResult();
          const claims = idTokenResult.claims || {};
          setTokenClaims(claims);
          const site = hasSiteAccess(claims);
          setAuthorized(site);
          setIsApprover(claims.approver === true);
          if (site) {
            setActiveModule((m) => (m == null ? 'landing' : m));
          } else {
            setActiveModule(null);
          }
        } else {
          setAuthorized(false);
          setTokenClaims({});
          setActiveModule(null);
          setPhotos([]);
          setTagConfig({
            groups: [],
            tags: [],
            people: [],
            places: [],
            pets: [],
            general: [],
            events: [],
            holidays: [],
            cameras: [],
          });
        }
      } catch (e) {
        console.error('Auth state handler error:', e);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [loadPhotos, loadAvailableTags, loadStagingPhotos]);

  // Load gallery data only when the user opens the photo module (keeps recipe-only users off Storage).
  useEffect(() => {
    if (!user || !authorized || activeModule !== 'photos' || !canReadPhotos(tokenClaims)) {
      return undefined;
    }
    loadPhotos();
    loadAvailableTags();
    if (isApprover) {
      loadStagingPhotos();
    }
    return undefined;
  }, [
    user,
    authorized,
    activeModule,
    tokenClaims,
    isApprover,
    loadPhotos,
    loadAvailableTags,
    loadStagingPhotos,
  ]);

  // Sidebar assignment: load unsaved form from the first selected photo (bulk save uses same values for all).
  useEffect(() => {
    if (selectedPhotos.length === 0) {
      const empty = emptyFacetState();
      setAssignFacets(empty);
      assignFacetsRef.current = empty;
      setAssignmentSavedSig(facetStateSignature(empty));
      return;
    }
    const sel = toProductionGalleryPath(normalizeStoragePath(selectedPhotos[0]));
    const first = photos.find((p) => toProductionGalleryPath(normalizeStoragePath(p.path)) === sel);
    if (first) {
      const h = hydrateFacetsFromLegacyTags(first.metadata);
      setAssignFacets(h);
      assignFacetsRef.current = h;
      setAssignmentSavedSig(facetStateSignature(h));
    }
  }, [selectedPhotos, photos]);

  useEffect(() => {
    if (!user || !authorized || !galleryQuery.presentation) return undefined;
    setActiveModule('photos');
    if (galleryQuery.untagged) setUntaggedOnly(true);
    const rules = rulesFromGalleryFilters(galleryQuery.filters);
    if (rules.length) setFacetFilterRules(rules);
    return undefined;
  }, [user, authorized, galleryQuery]);

  useEffect(() => {
    if (!user || !authorized || !galleryQuery.deviceId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        params.delete('photo');
        const idToken = await user.getIdToken();
        const response = await fetch(`${FUNCTIONS_BASE_URL}/queryPhotos?${params.toString()}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const data = await readResponseJson(response);
        if (cancelled || !response.ok) return;
        const ids = new Set(
          (Array.isArray(data.photos) ? data.photos : [])
            .map((photo) => sha256FromPath(photo?.path))
            .filter(Boolean)
        );
        setDevicePhotoIds(ids);
        if (data.b2?.download) setB2Download(data.b2.download);
        if (data.heicPreview?.token) setHeicPreview(data.heicPreview);
      } catch (error) {
        if (!cancelled) console.error('Error loading device gallery:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authorized, galleryQuery]);

  // Filter gallery by row rules (AND across rows/facets, OR within each facet).
  useEffect(() => {
    let filtered = showHiddenView
      ? hiddenPhotos
      : photos.filter((photo) => photo?.metadata?.hidden !== true);
    const active = facetFilterRules.some((rule) => facetStateHasSelections(rule.filters));
    if (active) {
      filtered = filtered.filter((photo) =>
        photoMatchesFacetFilterRules(photo.metadata, facetFilterRules)
      );
    }
    if (untaggedOnly) {
      filtered = filtered.filter((photo) => photoIsUntagged(photo.metadata));
    }
    if (devicePhotoIds) {
      filtered = filtered.filter((photo) => devicePhotoIds.has(sha256FromPath(photo.path)));
    }
    setFilteredPhotos(filtered);
  }, [photos, hiddenPhotos, showHiddenView, facetFilterRules, untaggedOnly, devicePhotoIds]);

  useEffect(() => {
    if (!galleryQuery.play || activeModule !== 'photos' || presentationStartedRef.current) return undefined;
    if (galleryQuery.deviceId && !devicePhotoIds) return undefined;
    if (filteredPhotos.length === 0) return undefined;
    presentationStartedRef.current = true;
    const id = photoIdFromLocation();
    const match = id ? filteredPhotos.find((photo) => sha256FromPath(photo.path) === id) : null;
    setLightboxPath((match || filteredPhotos[0]).path);
    return undefined;
  }, [galleryQuery.play, galleryQuery.deviceId, activeModule, filteredPhotos, devicePhotoIds]);

  /** Resolve from gallery lists using normalized paths (avoids stale / mismatched `path` string equality). */
  const lightboxIndexRef = useRef(0);

  const lightboxPhoto = useMemo(() => {
    if (!lightboxPath) return null;
    const key = toProductionGalleryPath(normalizeStoragePath(lightboxPath));
    const match = (arr) =>
      arr.find((p) => toProductionGalleryPath(normalizeStoragePath(p.path)) === key);
    return match(filteredPhotos) || match(photos) || match(hiddenPhotos) || match(linkedPhoto ? [linkedPhoto] : []);
  }, [lightboxPath, filteredPhotos, photos, hiddenPhotos, linkedPhoto]);

  useEffect(() => {
    if (!lightboxPath) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxPath]);

  useEffect(() => {
    if (!lightboxPath) return undefined;
    const openKey = galleryPathKey(lightboxPath);
    const index = filteredPhotos.findIndex((photo) => galleryPathKey(photo.path) === openKey);
    if (index >= 0) lightboxIndexRef.current = index;
    return undefined;
  }, [lightboxPath, filteredPhotos]);

  useEffect(() => {
    if (!lightboxPath) return undefined;
    const linkedKey = linkedPhoto ? galleryPathKey(linkedPhoto.path) : '';
    const openKey = galleryPathKey(lightboxPath);
    if (linkedKey && linkedKey === openKey) return undefined;
    const stillVisible =
      filteredPhotos.some((photo) => galleryPathKey(photo.path) === openKey) ||
      photos.some((photo) => galleryPathKey(photo.path) === openKey) ||
      hiddenPhotos.some((photo) => galleryPathKey(photo.path) === openKey);
    if (!stillVisible && filteredPhotos.length === 0) setLightboxPath(null);
    return undefined;
  }, [lightboxPath, filteredPhotos, photos, hiddenPhotos, linkedPhoto]);

  const closeLightbox = useCallback(() => {
    setLightboxPath(null);
    setLinkedPhoto(null);
    openedShareIdRef.current = '';
    setPhotoQuery('');
  }, []);

  const copyShareLink = useCallback(async () => {
    const id = sha256FromPath(lightboxPath);
    if (!id) return;
    const url = new URL(window.location.href);
    url.searchParams.set('photo', id);
    url.hash = '';
    try {
      await navigator.clipboard.writeText(url.toString());
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setShareStatus('Could not copy the link.');
    }
  }, [lightboxPath]);

  const goLightboxPrev = useCallback(() => {
    setLightboxPath((path) => {
      if (!path || filteredPhotos.length === 0) return path;
      const index = filteredPhotos.findIndex((photo) => galleryPathKey(photo.path) === galleryPathKey(path));
      if (index < 0) {
        const fallback = Math.min(Math.max(lightboxIndexRef.current - 1, 0), filteredPhotos.length - 1);
        return filteredPhotos[fallback].path;
      }
      const next = index <= 0 ? filteredPhotos.length - 1 : index - 1;
      return filteredPhotos[next].path;
    });
  }, [filteredPhotos]);

  const goLightboxNext = useCallback(() => {
    setLightboxPath((path) => {
      if (!path || filteredPhotos.length === 0) return path;
      const index = filteredPhotos.findIndex((photo) => galleryPathKey(photo.path) === galleryPathKey(path));
      if (index < 0) {
        const fallback = Math.min(lightboxIndexRef.current, filteredPhotos.length - 1);
        return filteredPhotos[fallback].path;
      }
      const next = index >= filteredPhotos.length - 1 ? 0 : index + 1;
      return filteredPhotos[next].path;
    });
  }, [filteredPhotos]);

  useEffect(() => {
    if (!lightboxPath) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLightbox();
      } else if (e.key === 'ArrowLeft' && galleryQuery.display?.reverse !== false) {
        e.preventDefault();
        goLightboxPrev();
      } else if (e.key === 'ArrowRight' && galleryQuery.display?.forward !== false) {
        e.preventDefault();
        goLightboxNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxPath, goLightboxPrev, goLightboxNext, closeLightbox, galleryQuery]);

  useEffect(() => {
    const seconds = galleryQuery.display?.seconds;
    if (!seconds || !lightboxPath) return undefined;
    const timer = window.setInterval(() => {
      goLightboxNext();
    }, seconds * 1000);
    return () => window.clearInterval(timer);
  }, [galleryQuery, lightboxPath, goLightboxNext]);

  useEffect(() => {
    const seconds = galleryQuery.display?.seconds;
    const showCountdown = galleryQuery.display?.countdown === true;
    if (!showCountdown || !seconds || !lightboxPath) {
      setSlideRemaining(0);
      return undefined;
    }
    setSlideRemaining(seconds);
    const timer = window.setInterval(() => {
      setSlideRemaining((value) => (value <= 1 ? seconds : value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [galleryQuery, lightboxPath]);

  useEffect(() => {
    if (!lightboxPath) return undefined;
    const id = sha256FromPath(lightboxPath);
    if (id) setPhotoQuery(id);
    return undefined;
  }, [lightboxPath]);

  useEffect(() => {
    if (!user || !authorized) return undefined;
    const id = photoIdFromLocation();
    if (!id || openedShareIdRef.current === id) return undefined;
    const existing = [...photos, ...hiddenPhotos].find((photo) => sha256FromPath(photo.path) === id);
    if (existing) {
      openedShareIdRef.current = id;
      setLinkedPhoto(existing);
      setLightboxPath(existing.path);
      setActiveModule('photos');
      setShareStatus('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${FUNCTIONS_BASE_URL}/getPhotoById?id=${encodeURIComponent(id)}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const data = await readResponseJson(response);
        if (cancelled) return;
        if (!response.ok || !data.photo?.path) {
          setShareStatus('That photo is not available.');
          return;
        }
        const mediaPath = normalizeStoragePath(data.photo.path);
        const photo = {
          ...data.photo,
          path: mediaPath,
          metadata: normalizeMetadataObject(data.photo.metadata),
        };
        openedShareIdRef.current = id;
        setLinkedPhoto(photo);
        setLightboxPath(mediaPath);
        setActiveModule('photos');
        setShareStatus('');
        if (data.b2?.download) setB2Download(data.b2.download);
        if (data.heicPreview?.token) setHeicPreview(data.heicPreview);
      } catch (error) {
        if (!cancelled) setShareStatus('That photo is not available.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authorized, photos, hiddenPhotos]);

  const signInWithGoogle = async () => {
    try {
      try {
        sessionStorage.removeItem(SKIP_AUTO_AUTH_KEY);
        sessionStorage.removeItem(AUTO_AUTH_ATTEMPTED_KEY);
      } catch (e) {
        /* ignore */
      }
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      alert('Failed to sign in. Please contact the administrator.');
    }
  };


  const handleSignOut = async () => {
    try {
      try {
        sessionStorage.setItem(SKIP_AUTO_AUTH_KEY, '1');
        sessionStorage.removeItem(AUTO_AUTH_ATTEMPTED_KEY);
      } catch (e) {
        /* ignore */
      }
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleApprovePhoto = async (photo) => {
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${FUNCTIONS_BASE_URL}/approvePhoto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          photoPath: photo.path
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to approve photo');
      }

      // Remove from staging and reload
      setStagingPhotos(prev => prev.filter(p => p.path !== photo.path));
      loadPhotos(); // Reload approved photos
      alert('Photo approved and added to gallery!');
    } catch (error) {
      console.error('Error approving photo:', error);
      alert(`Error approving photo: ${error.message}`);
    }
  };

  const handleRejectPhoto = async (photo) => {
    if (!window.confirm('Are you sure you want to reject this photo? It will be deleted.')) {
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${FUNCTIONS_BASE_URL}/rejectPhoto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          photoPath: photo.path
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reject photo');
      }

      // Remove from staging
      setStagingPhotos(prev => prev.filter(p => p.path !== photo.path));
      alert('Photo rejected and deleted.');
    } catch (error) {
      console.error('Error rejecting photo:', error);
      alert(`Error rejecting photo: ${error.message}`);
    }
  };

  const uploadFiles = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      if (!user) {
        throw new Error('You must be signed in to upload.');
      }
      if (!canUploadPhotos(tokenClaims)) {
        throw new Error('You do not have permission to upload photos.');
      }
      // Refresh token so Storage security rules see a valid, current credential.
      await user.getIdToken(true);

      const sanitizeName = (name) => {
        const base = (name || 'upload').split(/[/\\]/).pop();
        return base.replace(/[#*?]/g, '_').replace(/\[/g, '_').replace(/\]/g, '_');
      };

      const uploadOneWithProgress = (incomingRef, file, contentType) =>
        new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(incomingRef, file, { contentType });
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const total =
                snapshot.totalBytes > 0 ? snapshot.totalBytes : (file.size || 0);
              const transferred = snapshot.bytesTransferred;
              const pct =
                total > 0
                  ? Math.min(100, (transferred / total) * 100)
                  : snapshot.state === 'success'
                    ? 100
                    : 0;
              setUploadProgress(pct);
            },
            (error) => {
              console.error('Upload error:', error);
              reject(error);
            },
            () => resolve()
          );
        });

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uniqueName = `${Date.now()}-${i}-${sanitizeName(file.name)}`;
        const incomingRoot = isSupportedVideoName(file.name) ? 'videos/incoming' : 'photos/incoming';
        const incomingRef = ref(storage, `${incomingRoot}/${uniqueName}`);
        const contentType = file.type || 'application/octet-stream';

        try {
          await uploadOneWithProgress(incomingRef, file, contentType);
        } catch (firstErr) {
          if (firstErr?.code === 'storage/retry-limit-exceeded') {
            console.warn('Resumable upload hit retry limit; trying single-request upload.', firstErr);
            setUploadProgress(0);
            await uploadBytes(incomingRef, file, { contentType });
            setUploadProgress(100);
          } else {
            throw firstErr;
          }
        }
      }

      alert(
        'Upload complete. Processing usually finishes within a minute and the gallery will refresh a few times on its own. If nothing appears, refresh the page. Approvers: use Review Staging if uploads still require approval before they show for everyone.'
      );
      setUploadProgress(0);
      const reloadDelaysMs = [4000, 12000, 30000];
      reloadDelaysMs.forEach((ms) => {
        setTimeout(() => loadPhotos(), ms);
      });
      if (isApprover) {
        reloadDelaysMs.forEach((ms) => {
          setTimeout(() => loadStagingPhotos(), ms);
        });
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      const code = error?.code ? `${error.code}: ` : '';
      const msg = error?.message || 'Please try again.';
      const hint =
        error?.code === 'storage/retry-limit-exceeded'
          ? ' This usually means repeated network or permission failures (e.g. Storage rules blocking writes to incoming media folders). Check DevTools → Network for 403 on firebasestorage.googleapis.com.'
          : '';
      alert(`Error uploading files. ${code}${msg}${hint}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    await uploadFiles(files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(file => {
      const type = file.type.toLowerCase();
      const name = file.name.toLowerCase();
      const extension = name.split('.').pop();
      const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', '3gp'];
      return type.startsWith('image/') || type.startsWith('video/') || supportedExtensions.includes(extension);
    });

    if (files.length > 0) {
      await uploadFiles(files);
    }
  };

  const handlePhotoSelect = (photo) => {
    if (selectedPhotos.includes(photo.path)) {
      setSelectedPhotos(selectedPhotos.filter((path) => path !== photo.path));
    } else {
      setSelectedPhotos([...selectedPhotos, photo.path]);
    }
  };

  const persistFacetsForPaths = useCallback(
    async (pathsRaw, facets, options = {}) => {
      const { clearFlatTagsAndExtras = false, silent = false } = options;
      if (!user || pathsRaw.length === 0) return false;
      if (!canWritePhotoMetadata(tokenClaims)) {
        setFacetSidebarMessage('You do not have permission to edit photo metadata.');
        return false;
      }
      const payload = {
        people: normalizeStringList(facets.people),
        places: normalizeStringList(facets.places),
        pets: normalizeStringList(facets.pets),
        general: normalizeStringList(facets.general),
        events: normalizeStringList(facets.events),
        holidays: normalizeStringList(facets.holidays),
        cameras: normalizeCameraList(facets.cameras),
        ...(clearFlatTagsAndExtras
          ? {
              tags: [],
              albums: [],
              location: '',
              event: '',
            }
          : {}),
      };
      setFacetSidebarSaving(true);
      setFacetSidebarMessage(null);
      try {
        const idToken = await user.getIdToken();
        const paths = pathsRaw.map((p) => toProductionGalleryPath(normalizeStoragePath(p)));
        if (paths.length === 0) {
          setFacetSidebarSaving(false);
          return false;
        }
        let response;
        if (paths.length === 1) {
          response = await fetch(`${FUNCTIONS_BASE_URL}/updatePhotoMetadata`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ photoPath: paths[0], ...payload }),
          });
        } else {
          response = await fetch(`${FUNCTIONS_BASE_URL}/bulkUpdatePhotoMetadata`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ photoPaths: paths, ...payload }),
          });
        }
        const data = await readResponseJson(response);
        if (!response.ok) {
          const detail =
            Array.isArray(data.invalidValues) && data.invalidValues.length > 0
              ? `${data.error || 'Invalid values'}: ${data.invalidValues.join(', ')}`
              : data.error || `Failed to save (${response.status})`;
          throw new Error(detail);
        }
        if (
          paths.length > 1 &&
          typeof data.updated === 'number' &&
          data.updated === 0
        ) {
          throw new Error(
            (Array.isArray(data.errors) && data.errors.join('; ')) ||
              'No media was updated. Check production paths and try again.'
          );
        }

        const originalsSet = new Set(paths);
        const applySaved = (list) => {
          if (paths.length === 1 && data.metadata && typeof data.metadata === 'object') {
            const op = paths[0];
            return list.map((p) =>
              toProductionGalleryPath(normalizeStoragePath(p.path)) === op
                ? {
                    ...p,
                    metadata: normalizeMetadataObject({ ...(p.metadata || {}), ...data.metadata }),
                  }
                : p
            );
          }
          return list.map((p) => {
            if (!originalsSet.has(toProductionGalleryPath(normalizeStoragePath(p.path)))) return p;
            const m = normalizeMetadataObject(p.metadata || {});
            return {
              ...p,
              metadata: {
                ...m,
                people: [...payload.people],
                places: [...payload.places],
                pets: [...payload.pets],
                general: [...payload.general],
                events: [...payload.events],
                holidays: [...payload.holidays],
                cameras: [...payload.cameras],
                ...(clearFlatTagsAndExtras
                  ? {
                      tags: [],
                      albums: [],
                      location: '',
                      event: '',
                    }
                  : {}),
              },
            };
          });
        };
        if (paths.length === 1 && !(data.metadata && typeof data.metadata === 'object')) {
          console.warn(
            'updatePhotoMetadata: success but missing metadata in response; merging facets from the request into local state only.'
          );
        }
        setPhotos(applySaved);
        setHiddenPhotos(applySaved);
        if (!silent) {
          setFacetSidebarMessage(clearFlatTagsAndExtras ? 'Metadata cleared' : 'Saved');
        }
        window.setTimeout(() => setFacetSidebarMessage(null), 2000);
        // Do not call loadPhotos() here: a full refetch can return CDN-cached sidecar JSON and
        // overwrite the correct metadata we just merged from the API / payload, breaking filters
        // until a hard refresh. Single-save uses response metadata; bulk-save merges payload above.
        return true;
      } catch (err) {
        console.error('Facet save:', err);
        const msg = err?.message || 'Could not save';
        if (!silent) {
          setFacetSidebarMessage(msg);
          alert(msg);
        }
        return false;
      } finally {
        setFacetSidebarSaving(false);
      }
    },
    [user, tokenClaims]
  );

  const setPhotoHiddenFlag = useCallback(
    async (photo, hidden) => {
      if (!user || !hiddenAccess.canHide || !photo?.path) return false;
      setHiddenSaving(true);
      setHiddenMessage('');
      try {
        const idToken = await user.getIdToken();
        const path = galleryPathKey(photo.path);
        const response = await fetch(`${FUNCTIONS_BASE_URL}/setPhotoHidden`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ photoPath: path, hidden }),
        });
        const data = await readResponseJson(response);
        if (!response.ok) throw new Error(data.error || 'Could not update hidden photos');
        const next = {
          ...photo,
          path,
          metadata: { ...normalizeMetadataObject(photo.metadata), hidden },
        };
        if (hidden) {
          setPhotos((prev) => prev.filter((item) => galleryPathKey(item.path) !== path));
          setHiddenPhotos((prev) => [next, ...prev.filter((item) => galleryPathKey(item.path) !== path)]);
        } else {
          setHiddenPhotos((prev) => prev.filter((item) => galleryPathKey(item.path) !== path));
          setPhotos((prev) => [next, ...prev.filter((item) => galleryPathKey(item.path) !== path)]);
        }
        if (lightboxPath && galleryPathKey(lightboxPath) === path) {
          const rest = filteredPhotos.filter((item) => galleryPathKey(item.path) !== path);
          if (rest.length === 0) {
            setLinkedPhoto(next);
          } else {
            const index = filteredPhotos.findIndex((item) => galleryPathKey(item.path) === path);
            const neighbor = rest[Math.min(Math.max(index, 0), rest.length - 1)];
            setLinkedPhoto(null);
            setLightboxPath(neighbor.path);
          }
        }
        return true;
      } catch (err) {
        setHiddenMessage(err?.message || 'Could not update hidden photos');
        return false;
      } finally {
        setHiddenSaving(false);
      }
    },
    [user, hiddenAccess.canHide, lightboxPath, filteredPhotos]
  );

  const saveHiddenViewers = useCallback(
    async (emails) => {
      if (!user || !hiddenAccess.canHide) return;
      setHiddenSaving(true);
      setHiddenMessage('');
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${FUNCTIONS_BASE_URL}/updateHiddenViewers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ emails }),
        });
        const data = await readResponseJson(response);
        if (!response.ok) throw new Error(data.error || 'Could not update who can see hidden photos');
        setHiddenAccess((prev) => ({ ...prev, viewers: Array.isArray(data.viewers) ? data.viewers : [] }));
        setViewerDraft('');
      } catch (err) {
        setHiddenMessage(err?.message || 'Could not update who can see hidden photos');
      } finally {
        setHiddenSaving(false);
      }
    },
    [user, hiddenAccess.canHide]
  );

  const onAssignFacetsChange = useCallback((next) => {
    setAssignFacets(next);
    assignFacetsRef.current = next;
  }, []);

  const clearMetadataForSelection = useCallback(async () => {
    if (selectedPhotos.length === 0) return;
    const n = selectedPhotos.length;
    const ok = window.confirm(
      `Clear saved metadata on ${n} selected photo(s)?\n\n` +
        'This removes people, places, pets, general, events, holidays, camera, flat tags, albums, location, and event from each sidecar JSON so you can set them again.'
    );
    if (!ok) return;
    const empty = emptyFacetState();
    const saved = await persistFacetsForPaths(selectedPhotos, empty, { clearFlatTagsAndExtras: true });
    if (saved) {
      setAssignFacets(empty);
      assignFacetsRef.current = empty;
      setAssignmentSavedSig(facetStateSignature(empty));
    }
  }, [selectedPhotos, persistFacetsForPaths]);

  const saveAssignmentFacets = useCallback(async () => {
    if (selectedPhotos.length === 0) return;
    const latest = assignFacetsRef.current || emptyFacetState();
    const ok = await persistFacetsForPaths(selectedPhotos, latest);
    if (ok) {
      setAssignmentSavedSig(facetStateSignature(latest));
      setSelectedPhotos([]);
    }
  }, [selectedPhotos, persistFacetsForPaths]);

  if (loading) {
    return (
      <div className="app app--dark-ui">
        <div className="loading loading--splash">
          <h1 className="loading-site-name">Whitson Family</h1>
          <p className="loading-text">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app app--dark-ui app-auth-layout">
        <header className="header">
          <div className="header-actions">
            <button
              type="button"
              onClick={signInWithGoogle}
              className="btn btn-google btn-icon btn-google--pulse"
              title="Sign in with Google"
              aria-label="Sign in with Google"
            >
              <PersonIcon />
            </button>
          </div>
        </header>
        <div className="login-container">
          <div className="login-box">
            <h2 className="login-box-title">Welcome</h2>
            <p>
              Use your Google account to sign in. Click the <strong>person icon</strong> in the top
              right to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="app app--dark-ui app-auth-layout">
        <header className="header">
          <div className="header-actions">
            <button
              type="button"
              onClick={handleSignOut}
              className="btn btn-secondary btn-icon"
              title="Sign out"
              aria-label="Sign out"
            >
              <SignOutIcon />
            </button>
          </div>
        </header>
        <div className="login-container">
          <div className="login-box">
            <h2 className="login-box-title">Access Pending Approval</h2>
            <p>
              You signed in successfully, but you must be approved by an administrator of the site
              before you can use it. The administrators have been notified that there is an access
              request pending their approval.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (authorized && activeModule === 'landing') {
    return (
      <div className="app app--dark-ui app-auth-layout">
        <header className="header">
          <div className="header-actions">
            <span className="header-user-email" title={user.email}>
              {user.email}
            </span>
            <button
              type="button"
              onClick={handleSignOut}
              className="btn btn-secondary btn-icon"
              title="Sign out"
              aria-label="Sign out"
            >
              <SignOutIcon />
            </button>
          </div>
        </header>
        <LandingPage
          claims={tokenClaims}
          userEmail={user.email}
          onOpenPhotos={() => setActiveModule('photos')}
          onOpenRecipes={() => setActiveModule('recipes')}
          onOpenForum={() => setActiveModule('forum')}
          onOpenContacts={() => setActiveModule('contacts')}
          onOpenCalendar={() => setActiveModule('calendar')}
        />
      </div>
    );
  }

  if (authorized && activeModule === 'recipes') {
    return (
      <div className="app app--dark-ui">
        <RecipesSection
          app={app}
          user={user}
          functionsBaseUrl={FUNCTIONS_BASE_URL}
          claims={tokenClaims}
          onBack={() => setActiveModule('landing')}
        />
      </div>
    );
  }

  if (authorized && activeModule === 'forum') {
    return (
      <div className="app app--dark-ui">
        <FamilyHubSection
          title="Forum"
          description="This area is coming soon. You will be able to post threads and family announcements here."
          icon="💬"
          onBack={() => setActiveModule('landing')}
        />
      </div>
    );
  }

  if (authorized && activeModule === 'contacts') {
    return (
      <div className="app app--dark-ui">
        <FamilyHubSection
          title="Contacts"
          description="This area is coming soon. A shared family address book will live here."
          icon="📇"
          onBack={() => setActiveModule('landing')}
        />
      </div>
    );
  }

  if (authorized && activeModule === 'calendar') {
    return (
      <div className="app app--dark-ui">
        <FamilyHubSection
          title="Calendar"
          description="This area is coming soon. Shared events and reminders will appear here."
          icon="📅"
          onBack={() => setActiveModule('landing')}
        />
      </div>
    );
  }

  if (authorized && activeModule === 'photos' && !canReadPhotos(tokenClaims)) {
    return (
      <div className="app app--dark-ui app-auth-layout">
        <header className="header">
          <HeaderBrand />
          <div className="header-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setActiveModule('landing')}>
              Home
            </button>
          </div>
        </header>
        <div className="login-container">
          <div className="login-box">
            <h2 className="login-box-title">Photos</h2>
            <p>You do not have permission to open the photo gallery.</p>
          </div>
        </div>
      </div>
    );
  }

  const useCategoryAssignment = selectedPhotos.length > 0 && !showStaging;

  const assignmentDirty = facetStateSignature(assignFacets) !== assignmentSavedSig;

  const updateFacetFilterRule = (ruleId, nextFilters) => {
    setFacetFilterRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId ? { ...rule, filters: nextFilters || emptyFacetState() } : rule
      )
    );
    setGalleryPage(1);
  };

  const toggleFacetFilterRuleOperator = (ruleId) => {
    setFacetFilterRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? { ...rule, operator: rule.operator === 'exclude' ? 'include' : 'exclude' }
          : rule
      )
    );
    setGalleryPage(1);
  };

  const addFacetFilterRule = () => {
    setFacetFilterRules((prev) => [...prev, createFacetFilterRule()]);
  };

  const removeFacetFilterRule = (ruleId) => {
    setFacetFilterRules((prev) => {
      const next = prev.filter((rule) => rule.id !== ruleId);
      return next.length > 0 ? next : [createFacetFilterRule()];
    });
    setGalleryPage(1);
  };

  const clearFacetFilterRules = () => {
    setFacetFilterRules([createFacetFilterRule()]);
    setGalleryPage(1);
  };

  return (
    <div
      className={`app app--dark-ui${lightboxPath ? ' app--lightbox-open' : ''}${galleryQuery.display?.tags === false ? ' app--hide-tags' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <header className="header">
        <HeaderBrand />
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setActiveModule('landing');
              setShowStaging(false);
              setSelectedPhotos([]);
              setLightboxPath(null);
            }}
          >
            Home
          </button>
          {selectedPhotos.length > 0 && (
            <span className="selection-count">
              {selectedPhotos.length} selected
            </span>
          )}
          {isApprover && (
            <button
              onClick={() => {
                setShowStaging(!showStaging);
                setSelectedPhotos([]);
              }}
              className={`btn ${showStaging ? 'btn-primary' : 'btn-secondary'}`}
            >
              {showStaging ? 'View Gallery' : `Review Staging (${stagingPhotos.length})`}
            </button>
          )}
          <button 
            className="btn btn-upload"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !canUploadPhotos(tokenClaims)}
            title={!canUploadPhotos(tokenClaims) ? 'You do not have permission to upload' : undefined}
          >
            {uploading ? `Uploading... ${Math.round(uploadProgress)}%` : 'Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.heic,.heif,.bmp"
            onChange={handleFileUpload}
            disabled={uploading || !canUploadPhotos(tokenClaims)}
            style={{ display: 'none' }}
          />
          {selectedPhotos.length > 0 && (
            <button
              onClick={() => {
                setSelectedPhotos([]);
              }}
              className="btn btn-secondary"
            >
              Clear selected
            </button>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            className="btn btn-secondary btn-icon"
            title={`Signed in as ${user.email}. Sign out.`}
            aria-label={`Sign out (${user.email})`}
          >
            <SignOutIcon />
          </button>
        </div>
      </header>

      {isDragging && (
        <div className="drag-overlay">
          <div className="drag-overlay-content">
            📤 Drop photos here to upload
          </div>
        </div>
      )}

      <main className={`main${!showStaging ? ' main--gallery-shell' : ''}`}>
        {/* Staging Area for Approvers */}
        {isApprover && showStaging && (
          <div className="staging-section">
            <h2>Staging - Awaiting Approval ({stagingPhotos.length})</h2>
            {stagingPhotos.length === 0 ? (
              <div className="empty-state">
                <p>No photos awaiting approval.</p>
              </div>
            ) : (
              <div className="photo-grid">
                {stagingPhotos.map((photo, index) => {
                  const mediaUrl = typeof photo.url === 'string' ? photo.url : '';
                  const isVideo = isVideoMedia(photo, mediaUrl);
                  return (
                  <div key={index} className="photo-item staging-item">
                    {isVideo ? (
                      <div className="photo-thumbnail photo-thumbnail--video-placeholder">
                        <span className="video-placeholder-icon" aria-hidden>
                          ▶
                        </span>
                        <span>Video</span>
                      </div>
                    ) : (
                      <img src={mediaUrl} alt={photo.name || ''} className="photo-thumbnail" />
                    )}
                    <div className="photo-overlay">
                      <button 
                        className="btn-approve"
                        onClick={() => handleApprovePhoto(photo)}
                        title="Approve"
                      >
                        ✓ Approve
                      </button>
                      <button 
                        className="btn-reject"
                        onClick={() => handleRejectPhoto(photo)}
                        title="Reject"
                      >
                        ✗ Reject
                      </button>
                    </div>
                    {photo.metadata?.originalFilename && (
                      <div className="photo-info">
                        <small>{photo.metadata.originalFilename}</small>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!showStaging && (
          <div className="gallery-page">
            <GalleryFilterSidebar
              facetOptions={facetOptions}
              facetFilterRules={facetFilterRules}
              onFacetFilterRuleChange={updateFacetFilterRule}
              onFacetFilterRuleOperatorToggle={toggleFacetFilterRuleOperator}
              onAddFacetFilterRule={addFacetFilterRule}
              onRemoveFacetFilterRule={removeFacetFilterRule}
              onClearFacetFilters={clearFacetFilterRules}
              untaggedOnly={untaggedOnly}
              onToggleUntagged={() => {
                setUntaggedOnly((on) => !on);
                setGalleryPage(1);
              }}
              selectedPhotoCount={selectedPhotos.length}
              onClearSelectedPhotos={() => setSelectedPhotos([])}
              useCategoryAssignment={useCategoryAssignment}
              assignFacets={assignFacets}
              onAssignFacetsChange={onAssignFacetsChange}
              assignmentSaving={facetSidebarSaving}
              assignmentMessage={facetSidebarMessage}
              onClearMetadata={clearMetadataForSelection}
              onSaveAssignment={saveAssignmentFacets}
              assignmentDirty={assignmentDirty}
              assignmentDisabled={!canWritePhotoMetadata(tokenClaims)}
            />
            <div className="gallery-content">
            <div className="gallery-page-main">
              {shareStatus && <p className="hidden-viewers-error">{shareStatus}</p>}
              {facetFilterChipEntries.length > 0 || untaggedOnly ? (
                <div className="gallery-active-bar" aria-label="Active filters">
                  <span className="gallery-active-label">Showing</span>
                  <div className="gallery-active-chips">
                    {untaggedOnly && (
                      <button
                        type="button"
                        className="gallery-chip"
                        onClick={() => {
                          setUntaggedOnly(false);
                          setGalleryPage(1);
                        }}
                      >
                        Untagged
                      </button>
                    )}
                    {facetFilterChipEntries.map(({ ruleId, k, label, v, displayValue }) => (
                      <button
                        key={`${ruleId}:${k}:${v}`}
                        type="button"
                        className="gallery-chip"
                        onClick={() =>
                          setFacetFilterRules((prev) =>
                            prev.map((rule) =>
                              rule.id === ruleId
                                ? {
                                    ...rule,
                                    filters: {
                                      ...rule.filters,
                                      [k]: (rule.filters?.[k] || []).filter((x) => x !== v),
                                    },
                                  }
                                : rule
                            )
                          )
                        }
                      >
                        <span className="gallery-chip-prefix">{label}:</span>{' '}
                        {facetFilterEntryLabel(displayValue)}
                        <span className="gallery-chip-x" aria-hidden>
                          ×
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="gallery-limit-row" aria-label="Thumbnail display limit">
                <label className="gallery-limit-label" htmlFor="gallery-thumb-limit">
                  Per page
                </label>
                <select
                  id="gallery-thumb-limit"
                  className="gallery-limit-select"
                  value={thumbnailsPerPage}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (!Number.isFinite(n) || n < 1) return;
                    setThumbnailsPerPage(Math.min(100, n));
                    setGalleryPage(1);
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
                <span className="gallery-limit-note">
                  {filteredPhotos.length === 0
                    ? 'No matching photos'
                    : `Showing ${currentPageStart + 1}-${Math.min(currentPageEnd, filteredPhotos.length)} of ${filteredPhotos.length}`}
                </span>
                {hiddenAccess.canView && (
                  <button
                    type="button"
                    className={`gallery-page-btn${showHiddenView ? ' gallery-page-btn--active' : ''}`}
                    onClick={() => {
                      setShowHiddenView((current) => !current);
                      setGalleryPage(1);
                      setHiddenMessage('');
                    }}
                  >
                    {showHiddenView ? 'Gallery' : `Hidden${hiddenPhotos.length ? ` (${hiddenPhotos.length})` : ''}`}
                  </button>
                )}
                <div className="gallery-pagination-controls" aria-label="Gallery pagination">
                  <button
                    type="button"
                    className="gallery-page-btn"
                    disabled={galleryPage <= 1}
                    onClick={() => setGalleryPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                  <span className="gallery-page-status">
                    Page {galleryPage} of {totalGalleryPages}
                  </span>
                  <button
                    type="button"
                    className="gallery-page-btn"
                    disabled={galleryPage >= totalGalleryPages}
                    onClick={() => setGalleryPage((p) => Math.min(totalGalleryPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>

              {showHiddenView && hiddenAccess.canHide && (
                <div className="hidden-viewers">
                  <div className="hidden-viewers-copy">
                    <strong>Who can see hidden photos</strong>
                    <span>You always can. Add a Gmail address for anyone else.</span>
                  </div>
                  <form
                    className="hidden-viewers-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const email = viewerDraft.trim().toLowerCase();
                      if (!email || hiddenSaving) return;
                      if (email === 'chuckwhitson@gmail.com') {
                        setHiddenMessage('You already see hidden photos.');
                        return;
                      }
                      if (hiddenAccess.viewers.includes(email)) {
                        setViewerDraft('');
                        return;
                      }
                      saveHiddenViewers([...hiddenAccess.viewers, email]);
                    }}
                  >
                    <input
                      type="email"
                      className="hidden-viewers-input"
                      placeholder="name@gmail.com"
                      value={viewerDraft}
                      onChange={(e) => setViewerDraft(e.target.value)}
                      aria-label="Email that can see hidden photos"
                    />
                    <button type="submit" className="gallery-page-btn" disabled={hiddenSaving || !viewerDraft.trim()}>
                      Add
                    </button>
                  </form>
                  {hiddenAccess.viewers.length > 0 && (
                    <div className="hidden-viewers-list">
                      {hiddenAccess.viewers.map((email) => (
                        <button
                          key={email}
                          type="button"
                          className="gallery-chip"
                          disabled={hiddenSaving}
                          onClick={() => saveHiddenViewers(hiddenAccess.viewers.filter((item) => item !== email))}
                        >
                          {email}
                          <span className="gallery-chip-x" aria-hidden>
                            ×
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {hiddenMessage && <p className="hidden-viewers-error">{hiddenMessage}</p>}
                </div>
              )}

              {filteredPhotos.length === 0 ? (
                <div className="empty-state empty-state--on-dark">
                  <p>
                    {showHiddenView
                      ? 'No hidden photos.'
                      : facetFilterChipEntries.length > 0
                        ? 'No photos match the current facet filters.'
                        : 'No photos found. Upload some photos to get started!'}
                  </p>
                </div>
              ) : (
                <div className="photo-grid photo-grid--gallery">
                  {visiblePhotos.map((photo, index) => {
                    const mediaPath = normalizeStoragePath(photo.path);
                    const useMirror = gcsFallback[mediaPath] === true;
                    const forceConvert = convertFallback[mediaPath] === true;
                    const heicUrl = heicPreviewUrl(heicPreview, mediaPath, useMirror, forceConvert);
                    const posterUrl = posterFailed[mediaPath] ? '' : videoPosterUrl(heicPreview, mediaPath, useMirror);
                    const b2PreviewPath =
                      !useMirror && !heicUrl && !posterUrl && isB2GalleryPath(mediaPath)
                        ? photo.thumbPath || (isVideoMedia(photo, '') ? '' : mediaPath)
                        : '';
                    const b2ThumbUrl = b2PreviewPath
                      ? b2AuthorizedFileUrl(b2Download, b2PreviewPath)
                      : '';
                    const mediaUrl =
                      heicUrl ||
                      posterUrl ||
                      b2ThumbUrl ||
                      mirrorUrlByPath[mediaPath] ||
                      (typeof photo.url === 'string' && photo.url) ||
                      mediaUrlByPath[mediaPath] ||
                      '';
                    const mediaLoadAttempts = mediaUrlAttemptsByPath[mediaPath] || 0;
                    const m = normalizeMetadataObject(photo.metadata);
                    const tagList = [
                      ...m.people,
                      ...m.places,
                      ...m.pets,
                      ...m.general,
                      ...m.events,
                      ...m.holidays,
                      ...m.cameras,
                      ...normalizeTagArray(m.tags),
                    ];
                    const isVideo = !posterUrl && !b2ThumbUrl && isVideoMedia(photo, mediaUrl);
                    return (
                      <div
                        key={photo.path || index}
                        role="button"
                        tabIndex={0}
                        className={`photo-item photo-item--gallery ${
                          selectedPhotos.includes(photo.path) ? 'selected' : ''
                        }${photo.metadata?.hidden ? ' photo-item--hidden' : ''}`}
                        onClick={(e) => {
                          if (e.target.closest('.photo-select-checkbox')) return;
                          if (e.target.closest('.photo-overlay')) return;
                          if (!mediaUrl) return;
                          setLightboxPath(photo.path);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            if (e.target.closest('.photo-select-checkbox')) return;
                            if (e.target.closest('.photo-overlay')) return;
                            if (!mediaUrl) return;
                            e.preventDefault();
                            setLightboxPath(photo.path);
                          }
                        }}
                      >
                        <div className="photo-select-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedPhotos.includes(photo.path)}
                            onChange={() => handlePhotoSelect(photo)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {!mediaUrl ? (
                          <div className="photo-thumbnail photo-thumbnail--loading">
                            {mediaLoadAttempts >= 4 ? 'Unavailable' : 'Loading...'}
                          </div>
                        ) : isVideo ? (
                          <div className="photo-thumbnail photo-thumbnail--video-placeholder">
                            <span className="video-placeholder-icon" aria-hidden>
                              ▶
                            </span>
                            <span>Video</span>
                          </div>
                        ) : (
                          <img
                            src={mediaUrl}
                            alt={photo.name || ''}
                            className="photo-thumbnail"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              if (posterUrl && !useMirror) {
                                setPosterFailed((prev) => ({ ...prev, [mediaPath]: true }));
                                if (isB2GalleryPath(mediaPath)) rememberGcsMirror(mediaPath);
                                return;
                              }
                              if (!useMirror && isB2GalleryPath(mediaPath) && b2PreviewPath && b2PreviewPath !== mediaPath) {
                                const fallback = b2AuthorizedFileUrl(b2Download, mediaPath);
                                if (fallback && e.currentTarget.src !== fallback) {
                                  e.currentTarget.src = fallback;
                                  return;
                                }
                              }
                              // A .jpg the browser cannot decode may be a HEIC; try the converter next.
                              if (!forceConvert && !heicUrl && heicPreview?.token && isConvertiblePath(mediaPath)) {
                                setConvertFallback((prev) => ({ ...prev, [mediaPath]: true }));
                                return;
                              }
                              if (!useMirror && isB2GalleryPath(mediaPath)) {
                                rememberGcsMirror(mediaPath, forceConvert ? 'preview' : undefined);
                              }
                            }}
                          />
                        )}
                        {photo.metadata?.hidden && <span className="photo-hidden-badge">Hidden</span>}
                        {tagList.length > 0 && (
                          <div className="photo-tags">
                            {tagList.map((tag) => (
                              <span key={tag} className="photo-tag">
                                {displayTagLabel(tag, pickerGroups)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {filteredPhotos.length > 0 && (
                <div className="gallery-bottom-pagination" aria-label="Gallery bottom pagination">
                  <button
                    type="button"
                    className="gallery-page-btn"
                    disabled={galleryPage <= 1}
                    onClick={() => setGalleryPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                  <label className="gallery-page-select-label" htmlFor="gallery-page-select-bottom">
                    Page
                  </label>
                  <select
                    id="gallery-page-select-bottom"
                    className="gallery-page-select"
                    value={galleryPage}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (!Number.isFinite(n)) return;
                      setGalleryPage(Math.min(totalGalleryPages, Math.max(1, n)));
                    }}
                  >
                    {galleryPageOptions.map((page) => (
                      <option key={page} value={page}>
                        {page}
                      </option>
                    ))}
                  </select>
                  <span className="gallery-page-status">of {totalGalleryPages}</span>
                  <button
                    type="button"
                    className="gallery-page-btn"
                    disabled={galleryPage >= totalGalleryPages}
                    onClick={() => setGalleryPage((p) => Math.min(totalGalleryPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
        )}
      </main>

      {lightboxPhoto && lightboxPath && (
        <div
          className="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={lightboxPhoto.name ? `Enlarged: ${lightboxPhoto.name}` : 'Enlarged photo'}
          onClick={() => closeLightbox()}
        >
          <button
            type="button"
            className="lightbox-close"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
          >
            ×
          </button>
          {galleryQuery.display?.countdown === true && slideRemaining > 0 && (
            <div className="lightbox-countdown" aria-live="polite">
              {slideRemaining}
            </div>
          )}
          {galleryQuery.display?.reverse !== false && (
          <button
            type="button"
            className="lightbox-nav lightbox-nav--prev"
            aria-label="Previous photo"
            onClick={(e) => {
              e.stopPropagation();
              goLightboxPrev();
            }}
          >
            ‹
          </button>
          )}
          <div
            className="lightbox-panel"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lightbox-media-wrap">
              {(() => {
                const lightboxStoragePath = normalizeStoragePath(lightboxPhoto.path);
                const lightboxMirror = gcsFallback[lightboxStoragePath] === true;
                const lightboxConvert = convertFallback[lightboxStoragePath] === true;
                const lightboxUrl =
                  heicPreviewUrl(heicPreview, lightboxPhoto.path, lightboxMirror, lightboxConvert) ||
                  mirrorUrlByPath[lightboxStoragePath] ||
                  (isB2GalleryPath(lightboxPhoto.path) && !lightboxMirror
                    ? b2AuthorizedFileUrl(b2Download, lightboxPhoto.path)
                    : typeof lightboxPhoto.url === 'string'
                      ? lightboxPhoto.url
                      : '');
                const onLightboxError = () => {
                  if (lightboxMirror) return;
                  if (
                    !lightboxConvert &&
                    !isHeicPath(lightboxPhoto.path) &&
                    heicPreview?.token &&
                    isConvertiblePath(lightboxPhoto.path)
                  ) {
                    setConvertFallback((prev) => ({ ...prev, [lightboxStoragePath]: true }));
                    return;
                  }
                  if (!isB2GalleryPath(lightboxPhoto.path)) return;
                  rememberGcsMirror(
                    lightboxPhoto.path,
                    isHeicPath(lightboxPhoto.path) || lightboxConvert ? 'preview' : 'file'
                  );
                };
                if (isVideoMedia(lightboxPhoto, lightboxUrl)) {
                  return (
                    <video
                      src={lightboxUrl}
                      controls
                      className="lightbox-media"
                      preload="metadata"
                      playsInline
                      onError={onLightboxError}
                    />
                  );
                }
                return (
                  <img
                    src={lightboxUrl}
                    alt={lightboxPhoto.name || ''}
                    className="lightbox-media"
                    referrerPolicy="no-referrer"
                    onError={onLightboxError}
                  />
                );
              })()}
            </div>
            <div className="lightbox-side">
              {sha256FromPath(lightboxPhoto.path) && (
                <div className="lightbox-share">
                  <button type="button" className="gallery-page-btn lightbox-hide-btn" onClick={copyShareLink}>
                    {linkCopied ? 'Link copied' : 'Copy link'}
                  </button>
                  <p>Anyone signed in to the gallery can open this photo from the link.</p>
                </div>
              )}
              {hiddenAccess.canHide && (
                <div className="lightbox-hide">
                  <button
                    type="button"
                    className="gallery-page-btn lightbox-hide-btn"
                    disabled={hiddenSaving}
                    onClick={() =>
                      setPhotoHiddenFlag(lightboxPhoto, lightboxPhoto.metadata?.hidden !== true)
                    }
                  >
                    {lightboxPhoto.metadata?.hidden ? 'Unhide' : 'Hide from gallery'}
                  </button>
                  <p>
                    {lightboxPhoto.metadata?.hidden
                      ? 'Only you and the people you add can see this. Unhide puts it back in the gallery.'
                      : 'The file stays in storage. Everyone else stops seeing it in the gallery.'}
                  </p>
                  {hiddenMessage && <p className="hidden-viewers-error">{hiddenMessage}</p>}
                </div>
              )}
              {galleryQuery.display?.tags !== false && (
              <LightboxTagEditor
                photo={lightboxPhoto}
                facetOptions={facetOptions}
                canEdit={canWritePhotoMetadata(tokenClaims)}
                saving={facetSidebarSaving}
                onSave={(facets) =>
                  persistFacetsForPaths([lightboxPhoto.path], facets, { silent: true })
                }
              />
              )}
            </div>
          </div>
          {galleryQuery.display?.forward !== false && (
          <button
            type="button"
            className="lightbox-nav lightbox-nav--next"
            aria-label="Next photo"
            onClick={(e) => {
              e.stopPropagation();
              goLightboxNext();
            }}
          >
            ›
          </button>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
